import { useLocation } from "@remix-run/react";
import { useEffect, useRef } from "react";

import { ADMIN_PATH_RE, documentVisitId, sendVisitBeacon } from "./beacon";

/** El snippet ya confirmó este documento (204 del POST o el GIF del `<img>`: `window.__acdR`). */
const snippetConfirmed = () => (window as Window & { __acdR?: unknown }).__acdR === 1;

const isShown = () =>
  document.visibilityState === "visible" &&
  !(document as Document & { prerendering?: boolean }).prerendering;

/**
 * Segundo canal del render del documento: si la app montó con la página a la vista, hubo render.
 * Manda el mismo `document` que el snippet (`visit-render-snippet.ts`) con el id de
 * `window.__acdVid`, salvo que el snippet ya lo haya confirmado (`window.__acdR`). Sale por POST y
 * por `<img>` a la vez (`sendVisitBeacon`); la API es idempotente: si llegan todos, no pasa nada.
 * Si la página está oculta o precargada, espera a que se vea (y marca `hiddenFirst`).
 *
 * Existe por el WebView de TikTok Android cuando la red nativa (TTNet) trae el HTML: la app corría
 * entera (sus GET a la API, XHR de axios, llegaban) pero el beacon POST del snippet no
 * (diagnóstico del 2026-10-07).
 * Devuelve la función que saca los listeners.
 */
export function confirmDocumentRender(path: string): () => void {
  const visitId = documentVisitId();
  if (!visitId || ADMIN_PATH_RE.test(path) || snippetConfirmed()) return () => undefined;

  let hiddenFirst = false;
  let done = false;
  const tryNow = () => {
    if (done || !isShown()) return;
    done = true;
    cleanup();
    if (snippetConfirmed()) return;
    sendVisitBeacon({ kind: "document", visitId, path, hiddenFirst });
  };
  const cleanup = () => {
    document.removeEventListener("visibilitychange", tryNow);
    document.removeEventListener("prerenderingchange", tryNow);
  };

  if (!isShown()) {
    hiddenFirst = true;
    document.addEventListener("visibilitychange", tryNow);
    document.addEventListener("prerenderingchange", tryNow);
  }
  tryNow();
  return cleanup;
}

/**
 * Beacons del registro de visitas desde la app:
 *
 * - Primer montaje: confirma el render del documento (`confirmDocumentRender`).
 * - Cambio de ruta dentro de la app (sin documento nuevo): beacon `spa`, una página vista más. El
 *   primer render no manda `spa`: esa página ya la contó el documento.
 *
 * No depende del consentimiento ni de la config: es el registro propio (first-party, sin
 * terceros). La API sólo cuenta un `spa` si el navegador tiene `acd_aid` y la sesión tuvo un
 * documento real. Nunca en /admin.
 */
export function VisitBeacon() {
  const { pathname } = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    try {
      return confirmDocumentRender(window.location.pathname);
    } catch {
      // La medición nunca rompe la página.
    }
  }, []);

  useEffect(() => {
    if (previous.current === null) {
      previous.current = pathname;
      return;
    }
    if (previous.current === pathname) return;
    previous.current = pathname;
    if (ADMIN_PATH_RE.test(pathname)) return;
    sendVisitBeacon({ kind: "spa", path: pathname });
  }, [pathname]);

  return null;
}
